import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useHighlight } from "@/hooks/use-highlight";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { usePermissions } from "@/hooks/use-permissions";
import { useProjectPermission } from "@/hooks/use-project-permission";
import { useApproveTask, useRejectTask, useUpdateAssignment } from "@/hooks/use-my-tasks";
import { DeadlineBadge } from "@/components/projects/DeadlineBadge";
import { getProjectTasks, filterByStatus, type ProjectTaskStatus } from "@/lib/project-tasks";
import type { BudgetLineRow } from "@/hooks/use-project-detail";
import type { ProjectPhase } from "@/lib/offer-types";
import type { UserLookup } from "@/hooks/use-users";
import { getPhaseColor } from "@/lib/db-types";
import { PHASE_LABELS } from "@/lib/offer-types";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { CollapsibleCard } from "@/components/shared/CollapsibleCard";
import { TaskComments } from "./TaskComments";
import { useTaskCommentCounts } from "@/hooks/use-task-comments";

function HighlightedAssignmentRow({
  assignmentId,
  children,
}: {
  assignmentId: string;
  children: React.ReactNode;
}) {
  const ref = useHighlight<HTMLDivElement>(assignmentId);
  return <div ref={ref}>{children}</div>;
}

const TASK_STATUS_LABELS: Record<string, string> = {
  unassigned: "Ikke tildelt",
  not_started: "Ikke påbegynt",
  in_progress: "Påbegynt",
  completed: "Venter godkjenning",
  approved: "Godkjent",
  rejected: "Avvist",
};

const TASK_STATUS_COLORS: Record<string, string> = {
  unassigned: "bg-[#FFE1DB] text-[#7F1D1D] border-[1.5px] border-foreground",
  not_started: "bg-card text-foreground border-[1.5px] border-foreground",
  in_progress: "bg-[var(--sr-reveblaa)] text-white border-[1.5px] border-[var(--sr-reveblaa)]",
  completed: "bg-[#FEF3C7] text-[#78350F] border-[1.5px] border-foreground",
  approved: "bg-[#D9F5E8] text-[#035C39] border-[1.5px] border-foreground",
  rejected: "bg-[#FFE1DB] text-[#7F1D1D] border-[1.5px] border-foreground",
};

export default function TaskApprovalSection({
  budgetLines,
  userMap,
  projectId,
  loggedHoursByAssignment,
  loggedHoursByBudgetLine,
}: {
  budgetLines: BudgetLineRow[];
  userMap: Map<string, UserLookup>;
  projectId: string;
  loggedHoursByAssignment: Map<string, number>;
  loggedHoursByBudgetLine: Map<string, number>;
}) {
  const { activeUser: user } = useImpersonation();
  const { isSuperadmin } = usePermissions();
  // VIB-722c: approve_hours gates Godkjenn/Avvis-knappene. Admin/superadmin
  // overstyrer.
  const canApproveHours = useProjectPermission(projectId, "approve_hours");
  // Fri status-overstyring (DropdownMenu): superadmin har den alltid, og roller
  // som er gitt override_task_status i prosjektrolle-matrisen (PL/kunderådgiver
  // kan slås på midlertidig under «Tilganger & Roller»).
  const canOverrideStatus = useProjectPermission(projectId, "override_task_status");
  const approveMut = useApproveTask();
  const rejectMut = useRejectTask();
  const updateMut = useUpdateAssignment();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDeadline, setEditDeadline] = useState("");
  const [editNotes, setEditNotes] = useState("");

  // VIB-723: Bruk delt getProjectTasks-helper så Timetildeling og Godkjenning
  // viser samme sett. Inkluderer "unassigned"-rader (task-linjer uten
  // assignments) som PL trenger å se for å vite at noe mangler tildeling.
  const allTasks = getProjectTasks(budgetLines, loggedHoursByAssignment, loggedHoursByBudgetLine);
  const filtered = filterByStatus(allTasks, filter as ProjectTaskStatus | "all");
  const pendingCount = allTasks.filter((t) => t.status === "completed").length;
  const [openCommentId, setOpenCommentId] = useState<string | null>(null);
  const assignmentIdsForComments = filtered
    .map((t) => t.assignment?.id)
    .filter((id): id is string => !!id);
  const { data: commentCounts } = useTaskCommentCounts(assignmentIdsForComments);

  const openEdit = (a: NonNullable<typeof allTasks[number]["assignment"]>) => {
    setEditingId(a.id);
    setEditDeadline(a.estimated_deadline ?? "");
    setEditNotes(a.notes ?? "");
  };

  const handleSaveEdit = () => {
    if (!editingId) return;
    updateMut.mutate(
      { assignmentId: editingId, updates: { estimated_deadline: editDeadline || null, notes: editNotes || null } },
      { onSuccess: () => setEditingId(null) }
    );
  };

  const handleApprove = (assignmentId: string) => {
    if (!user?.id) return;
    approveMut.mutate({ assignmentId, approvedBy: user.id });
  };

  const handleReject = () => {
    if (!rejectingId || !rejectReason.trim()) return;
    rejectMut.mutate({ assignmentId: rejectingId, reason: rejectReason.trim() }, {
      onSuccess: () => { setRejectingId(null); setRejectReason(""); },
    });
  };

  /**
   * Manuell status-overstyring for superadmin. Setter approved_by/approved_at
   * når status går til 'approved'; nuller ut approval/rejection-felter ved
   * andre overganger så DB-en ikke står med stale felt.
   */
  const handleStatusChange = (assignmentId: string, newStatus: string) => {
    const updates: Record<string, any> = { task_status: newStatus };
    if (newStatus === "approved") {
      updates.approved_by = user?.id ?? null;
      updates.approved_at = new Date().toISOString();
      updates.rejection_reason = null;
    } else if (newStatus === "rejected") {
      updates.approved_by = null;
      updates.approved_at = null;
    } else {
      updates.approved_by = null;
      updates.approved_at = null;
      updates.rejection_reason = null;
    }
    updateMut.mutate({ assignmentId, updates });
  };

  return (
    <>
    <CollapsibleCard
      title="Oppgavestatus"
      icon={<SrIcon name="clipboard-check" className="w-5 h-5 text-primary" />}
      headerExtra={
        <>
          {pendingCount > 0 && (
            <span className="px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-700 rounded-full">
              {pendingCount} venter godkjenning
            </span>
          )}
          {allTasks.length > 0 && (
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-medium">
              {allTasks.length}
            </span>
          )}
        </>
      }
      actions={
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="text-xs h-7 gap-1">
              {filter === "all"
                ? `Alle (${allTasks.length})`
                : `${TASK_STATUS_LABELS[filter]} (${allTasks.filter((t) => t.status === filter).length})`}
              <SrIcon name="chevron-down" className="w-3 h-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            {[
              ["all", "Alle"],
              ["unassigned", "Ikke tildelt"],
              ["not_started", "Ikke påbegynt"],
              ["in_progress", "Påbegynt"],
              ["completed", "Venter godkjenning"],
              ["approved", "Godkjent"],
              ["rejected", "Avvist"],
            ].map(([key, label]) => {
              const count =
                key === "all"
                  ? allTasks.length
                  : allTasks.filter((t) => t.status === key).length;
              return (
                <DropdownMenuItem
                  key={key}
                  onClick={() => setFilter(key)}
                  className={cn("text-xs justify-between", filter === key && "font-semibold")}
                >
                  <span>{label}</span>
                  <span className="text-muted-foreground tabular-nums">{count}</span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      }
      contentClassName="border-t"
    >
          {allTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4">
              <p className="text-sm text-muted-foreground">Ingen oppgaver opprettet ennå. Legg til oppgavelinjer i budsjettet for å begynne.</p>
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">Ingen oppgaver i denne kategorien.</p>
          ) : (
            <div className="p-4 space-y-1">
              {filtered.map((t, idx) => {
                // Unassigned-rad: forenklet, ingen approve/reject, ingen kommentarer
                if (!t.assignment) {
                  return (
                    <div
                      key={`unassigned-${t.budgetLineId}-${idx}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-border/40 hover:bg-muted/20"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {t.phase && (
                            <Badge variant="secondary" className={cn("text-[10px]", getPhaseColor(t.phase))}>
                              {PHASE_LABELS[t.phase as ProjectPhase] ?? t.phase}
                            </Badge>
                          )}
                          <span className="text-sm font-medium truncate">{t.description}</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Ingen tildelt — fordel timer i Timetildeling-tabben</p>
                      </div>
                      <Badge variant="secondary" className={cn("text-[10px] shrink-0", TASK_STATUS_COLORS.unassigned)}>
                        {TASK_STATUS_LABELS.unassigned}
                      </Badge>
                    </div>
                  );
                }

                const a = t.assignment;
                const userName = userMap.get(a.user_id)?.full_name ?? "Ukjent";
                const commentCount = commentCounts?.get(a.id) ?? 0;
                const commentsOpen = openCommentId === a.id;
                return (
                  <HighlightedAssignmentRow key={a.id} assignmentId={a.id}>
                    {/* VIB-959 (fase 2): under xl stables raden (flex-col) i stedet
                        for å presse innhold + 4 handlingselementer inn i én
                        ikke-wrappende flex-rad — ansatt-flyt A-funn (godkjenne-fanen
                        hadde ingen mobiltilpasning). Handlings-wrapperen bruker
                        xl:contents så barna blir direkte flex-items i raden igjen
                        ved xl+ (identisk desktop-layout som før). */}
                    <div className="flex flex-col gap-2 px-3 py-2.5 rounded-md border border-border/40 hover:bg-muted/20 xl:flex-row xl:items-center xl:gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {t.phase && (
                            <Badge variant="secondary" className={cn("text-[10px]", getPhaseColor(t.phase))}>
                              {PHASE_LABELS[t.phase as ProjectPhase] ?? t.phase}
                            </Badge>
                          )}
                          <span className="text-sm font-medium truncate">{t.description}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-muted-foreground">{userName} · {a.hours}t tildelt · {t.loggedHours}t brukt</p>
                          {a.estimated_deadline && <DeadlineBadge deadline={a.estimated_deadline} />}
                        </div>
                        {a.notes && (
                          <p className="text-xs text-muted-foreground mt-0.5 italic">{a.notes}</p>
                        )}
                        {a.task_status === "rejected" && a.rejection_reason && (
                          <p className="text-xs text-destructive mt-0.5">Avvist: {a.rejection_reason}</p>
                        )}
                      </div>

                      <div className="flex items-center gap-2 flex-wrap xl:contents">
                        <button
                          onClick={() => setOpenCommentId(commentsOpen ? null : a.id)}
                          className={cn(
                            "inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded hover:bg-muted shrink-0 min-h-[40px] xl:min-h-0",
                            commentsOpen && "bg-muted text-primary",
                            commentCount > 0 && !commentsOpen && "text-primary",
                          )}
                          aria-expanded={commentsOpen}
                          title="Kommentarer"
                        >
                          <SrIcon name="message-square" className="w-3.5 h-3.5" />
                          {commentCount > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-bold rounded-full bg-primary text-primary-foreground tabular-nums">
                              {commentCount}
                            </span>
                          )}
                        </button>

                        <button
                          onClick={() => openEdit(a)}
                          className="min-h-[40px] min-w-[40px] xl:min-h-0 xl:min-w-0 inline-flex items-center justify-center p-1.5 rounded hover:bg-muted transition-colors shrink-0"
                          title="Rediger deadline og notater"
                        >
                          <SrIcon name="pencil" className="w-3.5 h-3.5 text-muted-foreground"  />
                        </button>

                        {(isSuperadmin || canOverrideStatus) ? (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className={cn(
                                  "inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium shrink-0 hover:opacity-80 min-h-[40px] xl:min-h-0",
                                  TASK_STATUS_COLORS[a.task_status],
                                )}
                                title="Endre status"
                              >
                                {TASK_STATUS_LABELS[a.task_status]}
                                <SrIcon name="chevron-down" className="w-3 h-3" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                              {(["not_started", "in_progress", "completed", "approved", "rejected"] as const).map((s) => (
                                <DropdownMenuItem
                                  key={s}
                                  onClick={() => handleStatusChange(a.id, s)}
                                  className={cn("text-xs", a.task_status === s && "font-semibold")}
                                >
                                  {TASK_STATUS_LABELS[s]}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        ) : (
                          <Badge variant="secondary" className={cn("text-xs px-2.5 py-1 shrink-0", TASK_STATUS_COLORS[a.task_status])}>
                            {TASK_STATUS_LABELS[a.task_status]}
                          </Badge>
                        )}

                        {/* Approve/reject buttons for completed tasks */}
                        {a.task_status === "completed" && canApproveHours && (
                          <div className="flex items-center gap-2 xl:gap-1 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-10 xl:h-7 text-xs px-2 text-green-700 border-green-300 hover:bg-green-50"
                              onClick={() => handleApprove(a.id)}
                              disabled={approveMut.isPending}
                            >
                              <SrIcon name="check" className="w-3 h-3 mr-1"  /> Godkjenn
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-10 xl:h-7 text-xs px-2 text-red-700 border-red-300 hover:bg-red-50"
                              onClick={() => { setRejectingId(a.id); setRejectReason(""); }}
                            >
                              <SrIcon name="x" className="w-3 h-3 mr-1"  /> Avvis
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                    {commentsOpen && (
                      <div className="ml-3 mr-3 mb-2">
                        <TaskComments assignmentId={a.id} />
                      </div>
                    )}
                  </HighlightedAssignmentRow>
                );
              })}
            </div>
          )}
    </CollapsibleCard>

      {/* Reject dialog */}
      <Dialog open={!!rejectingId} onOpenChange={(open) => { if (!open) setRejectingId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Avvis oppgave</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Skriv en tilbakemelding til den ansatte om hva som må gjøres.</p>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Tilbakemelding..."
              rows={3}
            />
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setRejectingId(null)}>Avbryt</Button>
              <Button size="sm" variant="destructive" onClick={handleReject} disabled={!rejectReason.trim() || rejectMut.isPending}>
                Avvis oppgave
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit deadline/notes dialog */}
      <Dialog open={!!editingId} onOpenChange={(open) => { if (!open) setEditingId(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rediger oppgave</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <SrIcon name="calendar-days" className="w-3.5 h-3.5"  /> Deadline
              </label>
              <Input
                type="date"
                value={editDeadline}
                onChange={(e) => setEditDeadline(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium flex items-center gap-1.5">
                <SrIcon name="sticky-note" className="w-3.5 h-3.5"  /> Notater
              </label>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Notater til oppgaveholderen..."
                rows={3}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setEditingId(null)}>Avbryt</Button>
              <Button size="sm" onClick={handleSaveEdit} disabled={updateMut.isPending}>Lagre</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
